import { isBooleanFalse, isBooleanTrue, isNumberGreaterThanOrEqualToZero, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { getNormalizedSurveyVersion } from "../utils/surveyVersionUtils";

// this key set is mirrored at three other sites - src/models/SurveyFlowItem.spec.js fails when they drift
export const SURVEY_FLOW_KEYS = Object.freeze({
    GOALS: 'GOALS',
    INTAKE: 'INTAKE',
    CONSULT: 'CONSULT',
    FOLLOW_UP: 'FOLLOW_UP',
    CHECK_IN: 'CHECK_IN',
    MEAL_PLAN: 'MEAL_PLAN',
});

export const normalizeSurveyFlowKey = (flowKey) => {
    if (!isStringNotEmpty(flowKey)) {
        return null;
    }

    const normalizedFlowKey = String(flowKey).trim().replace(/[\s-]+/g, '_').toUpperCase();
    if (normalizedFlowKey === 'FOLLOWUP') {
        return SURVEY_FLOW_KEYS.FOLLOW_UP;
    }

    return Object.values(SURVEY_FLOW_KEYS).includes(normalizedFlowKey) ? normalizedFlowKey : null;
};

/*
 * Which funnel types may fill a flow slot.
 *
 * Target validation has two halves. The existence half asks whether the slot resolves to a survey at
 * all; this is the suitability half - a CONSULT slot pointing at a goals funnel resolves perfectly
 * and still walks a patient into the wrong questionnaire. Nothing new is declared to answer it:
 * `Survey.funnelType` already records "the funnel that walks this survey", it is required when a
 * survey is created, and it is the field the check-in page has been gating on since it shipped. So
 * suitability is a rule over a field that exists rather than a field to add.
 *
 * GOALS and INTAKE are both SALES because they are two screens of one sales funnel - which is why
 * this is a list per slot rather than a one-to-one map. EVENT_CHECK_IN appears in no list: the event
 * check-in resolves its survey by id off the event record and has no flow key at all.
 *
 * src/models/SurveyFlowItem.spec.js pins both halves - every flow key has an entry, and every funnel
 * type named here is a real one - so a key added to the enum without an entry fails the build rather
 * than silently accepting any survey.
 */
export const SURVEY_FLOW_FUNNEL_TYPES = Object.freeze({
    GOALS: ['SALES'],
    INTAKE: ['SALES'],
    CONSULT: ['CONSULT'],
    FOLLOW_UP: ['FOLLOW_UP'],
    CHECK_IN: ['CHECK_IN'],
    MEAL_PLAN: ['MEAL_PLAN'],
});

export const SURVEY_FLOW_SUITABILITY = Object.freeze({
    SUITABLE: 'SUITABLE',
    UNDECLARED: 'UNDECLARED',
    MISMATCHED: 'MISMATCHED',
    UNKNOWN_FLOW: 'UNKNOWN_FLOW',
});

/*
 * UNDECLARED is a third answer rather than a quiet failure, and it has to stay one: `funnelType` is
 * newer than every survey in production, so reading a blank as unsuitable would refuse every live
 * funnel - which is the outage this check exists to prevent, not to cause. Only a declaration that
 * CONTRADICTS the slot is a finding. An unrecognised flow key is its own answer too, because a row
 * routing nothing is already reported as debris and must not also be reported as mis-targeted.
 */
export const getSurveyFlowSuitability = (flowKey, survey) => {
    const normalizedFlowKey = normalizeSurveyFlowKey(flowKey);
    if (isStringEmpty(normalizedFlowKey)) {
        return { status: SURVEY_FLOW_SUITABILITY.UNKNOWN_FLOW, flowKey: null, funnelType: null, acceptedFunnelTypes: [] };
    }

    const acceptedFunnelTypes = SURVEY_FLOW_FUNNEL_TYPES[normalizedFlowKey] ?? [];
    const funnelType = isStringNotEmpty(survey?.funnelType) ? survey.funnelType : null;
    if (funnelType === null) {
        return { status: SURVEY_FLOW_SUITABILITY.UNDECLARED, flowKey: normalizedFlowKey, funnelType: null, acceptedFunnelTypes };
    }

    return {
        status: acceptedFunnelTypes.includes(funnelType) ? SURVEY_FLOW_SUITABILITY.SUITABLE : SURVEY_FLOW_SUITABILITY.MISMATCHED,
        flowKey: normalizedFlowKey,
        funnelType,
        acceptedFunnelTypes,
    };
};

// what a host asks before walking a resolved survey: everything except an outright contradiction
export const isSurveyFlowTargetSuitable = (flowKey, survey) => {
    return getSurveyFlowSuitability(flowKey, survey).status !== SURVEY_FLOW_SUITABILITY.MISMATCHED;
};

export const getDuplicateSurveyFlowKeys = (surveyFlowItems = []) => {
    if (!Array.isArray(surveyFlowItems)) {
        return [];
    }

    const seenFlowKeys = new Set();
    const duplicateFlowKeys = new Set();
    surveyFlowItems.forEach((surveyFlowItem) => {
        const normalizedFlowKey = normalizeSurveyFlowKey(surveyFlowItem?.flowKey);
        if (isStringNotEmpty(normalizedFlowKey)) {
            if (seenFlowKeys.has(normalizedFlowKey)) {
                duplicateFlowKeys.add(normalizedFlowKey);
            } else {
                seenFlowKeys.add(normalizedFlowKey);
            }
        }
    });

    return Array.from(duplicateFlowKeys);
};

export const validateUniqueSurveyFlowKeys = (surveyFlowItems = [], context = 'Survey settings') => {
    const duplicateFlowKeys = getDuplicateSurveyFlowKeys(surveyFlowItems);
    if (duplicateFlowKeys.length > 0) {
        throw new Error(`${context} can only have one survey row per flow. Duplicate flow key(s): ${duplicateFlowKeys.join(', ')}.`);
    }

    return true;
};

class SurveyFlowItem {
    flowKey = null; // Enum SurveyFlowKeys
    surveyId = null;
    surveySlug = null; // the funnel this slot names - versions of it resolve by active + isDefault
    surveyVersion = null; // optional exact version label within surveySlug (matches the ?v= value)
    enabled = true;
    skippable = false;
    displayOrder = 0;

    constructor(surveyFlowItem, index = null) {
        if (isObjectNotEmpty(surveyFlowItem)) {
            const normalizedFlowKey = normalizeSurveyFlowKey(surveyFlowItem.flowKey);
            if (isStringNotEmpty(normalizedFlowKey)) { this.flowKey = normalizedFlowKey; }
            if (isStringNotEmpty(surveyFlowItem.surveyId)) { this.surveyId = surveyFlowItem.surveyId; }
            if (isStringNotEmpty(surveyFlowItem.surveySlug)) { this.surveySlug = surveyFlowItem.surveySlug; }
            this.surveyVersion = getNormalizedSurveyVersion(surveyFlowItem.surveyVersion);
            if (isBooleanFalse(surveyFlowItem.enabled)) { this.enabled = false; }
            else if (isBooleanTrue(surveyFlowItem.enabled)) { this.enabled = true; }
            if (isBooleanTrue(surveyFlowItem.skippable)) { this.skippable = true; }
            if (isNumberGreaterThanOrEqualToZero(surveyFlowItem.displayOrder)) { this.displayOrder = surveyFlowItem.displayOrder; }
            else if (isNumberGreaterThanOrEqualToZero(index)) { this.displayOrder = index + 1; }
        } else if (isNumberGreaterThanOrEqualToZero(index)) {
            this.displayOrder = index + 1;
        }
    }

    toDto = () => {
        return {
            flowKey: this.flowKey,
            surveyId: this.surveyId,
            surveySlug: this.surveySlug,
            surveyVersion: this.surveyVersion,
            enabled: this.enabled,
            skippable: this.skippable,
            displayOrder: this.displayOrder,
        };
    }
}

export const normalizeSurveyFlowItems = (surveyFlowItems = []) => {
    return Array.isArray(surveyFlowItems)
        ? surveyFlowItems.map((surveyFlowItem, index) => new SurveyFlowItem(surveyFlowItem, index).toDto())
        : [];
};

export default SurveyFlowItem;
